import React from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { DashboardProcessInstructionDto , DashboardProductionDetailDto } from '../../Dto_Home';

const PRODUCTION_COLUMN_STATE_KEY = 'home-b-production-detail-column-state';
const INSTRUCTION_COLUMN_STATE_KEY = 'home-b-process-instruction-column-state';
const ignoreDashboardGridState = () => undefined;

interface Props {
  productionDetailData: DashboardProductionDetailDto[];
  processInstructionData: DashboardProcessInstructionDto[];
}

// 컬럼 정의
const instructionColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params: any) => {
    if (params.node.rowPinned === 'bottom') return params.data?.rowIndex || '';
    return params.node.rowIndex + 1;
  }),
  { field: 'workplaceNm', headerName: '공정명', width: 120 ,
    cellStyle: (params) =>
      params.node.rowPinned === 'bottom'
        ? {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            fontWeight: 600,
          }
        : undefined,
  },
  { field: 'itemNm', headerName: '품명', width: 150 },
  { field: 'itemSpec', headerName: '규격', width: 120 },
  {
    field: 'orderQty',
    headerName: '지시량',
    width: 100,
    valueFormatter: (params: any) => {
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '');
    },
    cellStyle: { textAlign: 'right' }
  },
  { field: 'workEmpNm', headerName: '작업자', width: 100 },
  // { field: 'workDt', headerName: '작업일자', width: 120 },
];

// 컬럼 정의
const productionDetailColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params: any) => {
    if (params.node.rowPinned === 'bottom') return '';
    return params.node.rowIndex + 1;
  }),
  {
    field: 'groupKey',
    headerName: '생산일자',
    width: 150,
    valueFormatter: (params) => params.value || '-',
    cellStyle: (params) =>
      params.node.rowPinned === 'bottom'
        ? {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            fontWeight: 600,
          }
        : undefined,
  },
  {field: 'itemNm',headerName: '품목명',width: 120, },
  {field: 'itemSpec',headerName: '규격',width: 120,},
  {field: 'modelNo',headerName: '모델번호',width: 120,},
  {field: 'productQty',headerName: '생산량',width: 100,
  valueFormatter: (params: any) => {
    const value = params.value;
    return typeof value === 'number' ? value.toLocaleString() : (value || '');
  },
  cellStyle: { textAlign: 'right' }
  },
  {
    field: 'badQty',
    headerName: '불량',
    width: 100,
    
    cellStyle: { color: 'red', textAlign: 'right' }
  },
  {
    field: 'acceptQty',
    headerName: '입고량',
    width: 100,
    valueFormatter: (params: any) => {
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '');
    },
    cellStyle: { textAlign: 'right' }
    
  }
];
// 그리드 기본 설정
const defaultColDef: ColDef = { resizable: true, sortable: true };

const TablesSection_Pic: React.FC<Props> = ({
  productionDetailData, processInstructionData
}) => {
  const productionGridRef = useRef<AgGridReact<DashboardProductionDetailDto> | null>(null);
  const instructionGridRef = useRef<AgGridReact<DashboardProcessInstructionDto> | null>(null);

  //합계
  const productionPinnedBottomRow = useMemo(() => [{ 
    rowIndex: '', 
    groupKey: `${productionDetailData.length.toLocaleString()}건`,
    productQty: productionDetailData.reduce(
      (sum, item) => sum + (Number(item.productQty) || 0),
      0
    )
  }],
    [productionDetailData]
  );
    //합계
  const instructionPinnedBottomRow = useMemo(() => [{ 
    rowIndex: '', 
    workplaceNm: `${processInstructionData.length.toLocaleString()}건`,
    orderQty: processInstructionData.reduce(
      (sum, item) => sum + (Number(item.orderQty) || 0),
      0
    )
  }],
    [processInstructionData]
  );

  return (
    <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
      <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col">
      <h2 className="text-lg font-semibold mb-2">상세 생산실적</h2>
        <div className="ag-theme-alpine flex-1 min-h-0">
          <GridContextWrapper<DashboardProductionDetailDto>
            gridRef={productionGridRef}
            paddedGridData={productionDetailData}
            baseColumnDefs={productionDetailColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={PRODUCTION_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <Grid_Touch<DashboardProductionDetailDto>
              ref={productionGridRef}
              columnDefs={productionDetailColumns}
              rowData={productionDetailData}
              pinnedBottomRowData={productionPinnedBottomRow}
              defaultColDef={defaultColDef}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={PRODUCTION_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col">
        <h2 className="text-lg font-semibold mb-2">공정 지시내역</h2>
        <div className="ag-theme-alpine flex-1">
          <GridContextWrapper<DashboardProcessInstructionDto>
            gridRef={instructionGridRef}
            paddedGridData={processInstructionData}
            baseColumnDefs={instructionColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={INSTRUCTION_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <Grid_Touch<DashboardProcessInstructionDto>
              ref={instructionGridRef}
              rowData={processInstructionData}
              columnDefs={instructionColumns}
              pinnedBottomRowData={instructionPinnedBottomRow}
              defaultColDef={defaultColDef}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={INSTRUCTION_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
        </div>
      </div>
    </div>
  );
};

export default TablesSection_Pic;
